import { useMemo, useState } from 'react';
import { fromJS } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Select as TransitionalSelect } from 'foundations-components/transitional/components/Select';
import { useAuthInfoProvider } from 'content-management-data-lib';
import { CardWrapper, CardSection } from 'foundations-components/transitional/components/Card';
import { Select } from 'foundations-components/components/Select';
import { View } from 'foundations-components/components/View';
import { InlineLabel } from 'foundations-components/transitional/components/InlineLabel';
import { FilterBar } from 'foundations-components/transitional/components/FilterBar';
import PlatformReportResolver from 'reporting-ui-components/components/PlatformReport/PlatformReportResolver';
import { LINE } from 'reporting-data/constants/chartTypes';
import { TIME_SERIES } from 'reporting-data/constants/configTypes';
import { ANALYTICS_TARGETED_CONTENT } from 'reporting-data/constants/dataTypes';
import { EQ, IN } from 'reporting-data/constants/operators';
import { useReportingFiltersProvider } from '../../../providers/ReportingFiltersProvider';
import { getChartStyleOptions, isSecondaryMetric } from '../../../utils/reportingUtils';
import useReportingContentIds from '../../../hooks/useReportingContentIds';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getCustomFilters = ({
  contentId
}) => {
  return [{
    operator: IN,
    property: 'filters',
    values: ['targeted', 'default']
  }, {
    operator: EQ,
    property: 'pageId',
    value: contentId
  }];
};
const getReport = ({
  contentId,
  dateRange,
  d1,
  frequency,
  chartType,
  selectedMetric,
  source,
  allMetricsData
}) => {
  return fromJS({
    chartType,
    config: {
      configType: TIME_SERIES,
      dataType: ANALYTICS_TARGETED_CONTENT,
      dimensions: ['sessionDate', 'breakdown'],
      filters: {
        custom: getCustomFilters({
          contentId,
          d1,
          source
        }),
        dateRange: {
          property: 'sessionDate',
          value: dateRange
        }
      },
      frequency,
      metrics: [{
        metricTypes: allMetricsData[selectedMetric].metricTypes,
        property: selectedMetric
      }],
      sort: []
    },
    displayParams: {
      stacked: chartType !== LINE
    }
  });
};
const MetricSelector = ({
  allMetricsData,
  metricsToDisplay,
  handleChangeMetric,
  selectedMetric,
  isUngatedForCMPTrellisMigration
}) => {
  const options = useMemo(() => {
    return metricsToDisplay.map(m => ({
      value: m,
      text: I18n.text(allMetricsData[m].labelKey)
    }));
  }, [metricsToDisplay, allMetricsData]);
  if (isUngatedForCMPTrellisMigration) {
    return /*#__PURE__*/_jsx(View, {
      maxInlineSize: "300px",
      children: /*#__PURE__*/_jsx(Select, {
        onSelectedOptionChange: ({
          value
        }) => handleChangeMetric(value),
        options: options,
        selectedValues: [selectedMetric]
      })
    });
  }
  return /*#__PURE__*/_jsx(TransitionalSelect, {
    buttonUse: "transparent",
    onChange: e => handleChangeMetric(e.target.value),
    options: options,
    value: selectedMetric
  });
};
const ChartStyleSelector = ({
  chartStyle,
  handleChangeChartType,
  allMetricsData,
  selectedMetric,
  isUngatedForCMPTrellisMigration
}) => {
  const options = useMemo(() => {
    return getChartStyleOptions(allMetricsData[selectedMetric], isUngatedForCMPTrellisMigration);
  }, [allMetricsData, selectedMetric, isUngatedForCMPTrellisMigration]);
  if (isUngatedForCMPTrellisMigration) {
    return /*#__PURE__*/_jsx(InlineLabel, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "content-management-details-lib.reporting.sourcesChart.chartTypeSelectorLabel"
      }),
      minInlineSize: "175px",
      children: /*#__PURE__*/_jsx(Select, {
        onSelectedOptionChange: ({
          value
        }) => handleChangeChartType(value),
        options: options,
        selectedValues: [chartStyle]
      })
    });
  }
  return /*#__PURE__*/_jsx(InlineLabel, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "content-management-details-lib.reporting.sourcesChart.chartTypeSelectorLabel"
    }),
    children: /*#__PURE__*/_jsx(TransitionalSelect, {
      buttonUse: "link",
      onChange: e => handleChangeChartType(e.target.value),
      options: options,
      value: chartStyle
    })
  });
};
const SmartContentChart = () => {
  const {
    dateRange,
    d1,
    frequency,
    metric,
    metricsToDisplay,
    allMetricsData,
    setMetric,
    source
  } = useReportingFiltersProvider();
  const {
    contentId
  } = useReportingContentIds({});
  const {
    getIsUngatedForCMPTrellisMigration
  } = useAuthInfoProvider();
  const isUngatedForCMPTrellisMigration = getIsUngatedForCMPTrellisMigration();
  const [chartStyle, setChartStyle] = useState(LINE);
  const handleChangeMetric = newSelectedMetric => {
    setMetric(newSelectedMetric);
    if (chartStyle !== LINE) {
      const fullMetric = allMetricsData[newSelectedMetric];
      if (isSecondaryMetric(fullMetric)) {
        setChartStyle(LINE);
      }
    }
  };
  const report = getReport({
    contentId,
    dateRange,
    d1,
    frequency,
    chartType: chartStyle,
    selectedMetric: metric,
    source,
    allMetricsData
  });
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    children: [/*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(FilterBar, {
        flush: true,
        startSlot: /*#__PURE__*/_jsx(MetricSelector, {
          allMetricsData: allMetricsData,
          handleChangeMetric: handleChangeMetric,
          metricsToDisplay: metricsToDisplay,
          selectedMetric: metric,
          isUngatedForCMPTrellisMigration: isUngatedForCMPTrellisMigration
        }),
        endSlot: /*#__PURE__*/_jsx(ChartStyleSelector, {
          chartStyle: chartStyle,
          handleChangeChartType: setChartStyle,
          allMetricsData: allMetricsData,
          selectedMetric: metric,
          isUngatedForCMPTrellisMigration: isUngatedForCMPTrellisMigration
        })
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(PlatformReportResolver, {
        report: report,
        useInlineLabels: false
      })
    })]
  });
};
export default SmartContentChart;